/**
 * RepresentanteFormModal
 *
 * Epic 45: Email é agora o identificador único. CPF/CNPJ é opcional.
 *
 * Orchestrator: logic via useRepresentanteForm hook.
 */
import React from 'react';
import { X, Mail, Phone, User, Hash, Building2, Search, ArrowRight, ArrowLeft, MapPin, Loader2 } from 'lucide-react';
import { Representante } from '../services/representantesService';
import { useRepresentanteForm } from '../hooks/useRepresentanteForm';

interface RepresentanteFormModalProps {
  representante: Representante | null;
  clienteId?: number;
  onClose: () => void;
  onSave: () => void;
}

const RepresentanteFormModal: React.FC<RepresentanteFormModalProps> = ({
  representante,
  clienteId,
  onClose,
  onSave,
}) => {
  const {
    formData,
    setFormData,
    loading,
    errors,
    availableHospitais,
    associations,
    loadingHospitais,
    selectedAvailable,
    selectedLinked,
    searchLeft,
    setSearchLeft,
    searchRight,
    setSearchRight,
    linking,
    unlinking,
    filteredAvailable,
    filteredLinked,
    ConfirmDialog,
    handleCpfCnpjChange,
    handleTelefoneChange,
    handleLinkSelected,
    handleUnlinkSelected,
    toggleAvailableSelection,
    toggleLinkedSelection,
    toggleAllAvailable,
    toggleAllLinked,
    handleSubmit,
    formatDate,
  } = useRepresentanteForm({ representante, clienteId, onClose, onSave });

  return (
    <>
      <ConfirmDialog />
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              {representante ? 'Editar Representante' : 'Novo Representante'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-3 bg-white dark:bg-gray-800">
            {/* Nome e CPF/CNPJ */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Nome Completo <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className={`pl-9 pr-3 py-2 text-sm border rounded-md w-full focus:ring-1 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      errors.nome ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Nome do representante"
                  />
                </div>
                {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  CPF/CNPJ <span className="text-gray-400 text-xs font-normal">(opcional)</span>
                </label>
                <div className="relative">
                  <Hash className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={formData.cpf_cnpj}
                    onChange={(e) => handleCpfCnpjChange(e.target.value)}
                    className={`pl-9 pr-3 py-2 text-sm border rounded-md w-full focus:ring-1 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      errors.cpf_cnpj ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="000.000.000-00"
                    maxLength={18}
                  />
                </div>
                {errors.cpf_cnpj && <p className="text-red-500 text-xs mt-1">{errors.cpf_cnpj}</p>}
              </div>
            </div>

            {/* Telefone e Email */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Telefone <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={formData.telefone}
                    onChange={(e) => handleTelefoneChange(e.target.value)}
                    className={`pl-9 pr-3 py-2 text-sm border rounded-md w-full focus:ring-1 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      errors.telefone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                  />
                </div>
                {errors.telefone && <p className="text-red-500 text-xs mt-1">{errors.telefone}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Email <span className="text-red-500">*</span>{' '}
                  <span className="text-blue-500 text-xs font-normal">(identificador único)</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`pl-9 pr-3 py-2 text-sm border rounded-md w-full focus:ring-1 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="email@exemplo.com"
                  />
                </div>
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
            </div>

            {/* Dual-list de Hospitais */}
            {clienteId && representante && (
              <div className="pt-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    Gerenciar Hospitais
                  </h3>
                </div>

                {loadingHospitais ? (
                  <div className="py-12 flex flex-col items-center justify-center text-gray-500">
                    <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                    Carregando informações de hospitais...
                  </div>
                ) : (
                  <div className="flex gap-4 h-[400px]">
                    {/* Coluna Esquerda: Disponíveis */}
                    <div className="flex-1 flex flex-col border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
                      <div className="p-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                            Hospitais Disponíveis
                          </h4>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">
                            {filteredAvailable.length}
                          </span>
                        </div>
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                          <input
                            type="text"
                            value={searchLeft}
                            onChange={(e) => setSearchLeft(e.target.value)}
                            placeholder="Buscar disponíveis..."
                            className="w-full pl-8 pr-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        {filteredAvailable.length > 0 && (
                          <label className="flex items-center gap-1.5 mt-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
                            <input
                              type="checkbox"
                              checked={selectedAvailable.length === filteredAvailable.length}
                              onChange={toggleAllAvailable}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            Selecionar todos visíveis
                          </label>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {filteredAvailable.length === 0 ? (
                          <div className="flex items-center justify-center h-full text-xs text-gray-500 dark:text-gray-400">
                            {searchLeft ? 'Nenhum hospital encontrado' : 'Todos os hospitais já estão vinculados'}
                          </div>
                        ) : (
                          filteredAvailable.map((hospital) => (
                            <label
                              key={hospital.id}
                              className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                                selectedAvailable.includes(hospital.id)
                                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedAvailable.includes(hospital.id)}
                                onChange={() => toggleAvailableSelection(hospital.id)}
                                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-xs text-gray-900 dark:text-gray-100 truncate">
                                  {hospital.nome_fantasia}
                                </p>
                                {(hospital.codigo || hospital.cidade || hospital.estado) && (
                                  <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                    <MapPin className="w-3 h-3" />
                                    <span className="truncate">
                                      {[hospital.codigo, hospital.cidade, hospital.estado].filter(Boolean).join(' • ')}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Botões Centrais */}
                    <div className="flex flex-col items-center justify-center gap-3 w-24">
                      <button
                        onClick={handleLinkSelected}
                        disabled={selectedAvailable.length === 0 || linking}
                        className="w-full px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md flex items-center justify-center"
                        title="Vincular selecionados"
                        type="button"
                      >
                        {linking ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5 stroke-[2.5]" />}
                      </button>
                      <button
                        onClick={handleUnlinkSelected}
                        disabled={selectedLinked.length === 0 || unlinking}
                        className="w-full px-4 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md flex items-center justify-center"
                        title="Desvincular selecionados"
                        type="button"
                      >
                        {unlinking ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowLeft className="w-5 h-5 stroke-[2.5]" />}
                      </button>
                    </div>

                    {/* Coluna Direita: Vinculados */}
                    <div className="flex-1 flex flex-col border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
                      <div className="p-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                            Hospitais Vinculados
                          </h4>
                          <span className="text-xs font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded">
                            {filteredLinked.length}
                          </span>
                        </div>
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                          <input
                            type="text"
                            value={searchRight}
                            onChange={(e) => setSearchRight(e.target.value)}
                            placeholder="Buscar vinculados..."
                            className="w-full pl-8 pr-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        {filteredLinked.length > 0 && (
                          <label className="flex items-center gap-1.5 mt-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
                            <input
                              type="checkbox"
                              checked={selectedLinked.length === filteredLinked.length}
                              onChange={toggleAllLinked}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            Selecionar todos visíveis
                          </label>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {filteredLinked.length === 0 ? (
                          <div className="flex items-center justify-center h-full text-xs text-gray-500 dark:text-gray-400">
                            {searchRight ? 'Nenhum hospital encontrado' : 'Nenhum hospital vinculado'}
                          </div>
                        ) : (
                          filteredLinked.map((assoc) => {
                            const hospitalData = availableHospitais.find(h => h.id === assoc.hospital_id);
                            return (
                              <label
                                key={assoc.id ?? assoc.hospital_id}
                                className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                                  selectedLinked.includes(assoc.hospital_id)
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedLinked.includes(assoc.hospital_id)}
                                  onChange={() => toggleLinkedSelection(assoc.hospital_id)}
                                  className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <p className="font-medium text-xs text-gray-900 dark:text-gray-100 truncate flex-1">
                                      {assoc.hospital_nome}
                                    </p>
                                    <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium whitespace-nowrap">
                                      ✓
                                    </span>
                                  </div>
                                  {(hospitalData?.codigo || hospitalData?.cidade || hospitalData?.estado) && (
                                    <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                      <MapPin className="w-3 h-3" />
                                      <span className="truncate">
                                        {[hospitalData?.codigo, hospitalData?.cidade, hospitalData?.estado].filter(Boolean).join(' • ')}
                                      </span>
                                    </div>
                                  )}
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    Desde {formatDate(assoc.data_inicio)}
                                  </p>
                                </div>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Status bar */}
                {!loadingHospitais && (selectedAvailable.length > 0 || selectedLinked.length > 0) && (
                  <div className="mt-3 p-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      {selectedAvailable.length + selectedLinked.length} hospital(is) selecionado(s)
                      {selectedAvailable.length > 0 && ` · ${selectedAvailable.length} para vincular`}
                      {selectedLinked.length > 0 && ` · ${selectedLinked.length} para desvincular`}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 text-sm font-medium rounded-md flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Salvando...
                  </>
                ) : (
                  'Cadastrar Representante'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default RepresentanteFormModal;
